import { Button , Popover, PopoverContent, PopoverTrigger } from "@roberttamas/ui";
import { Command } from "cmdk";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Check, ChevronsUpDown } from "@/components/icons";
import { LocaleFlag } from "@/components/locale-flag";
import {
  formatLocaleLabel,
  getLocaleOption,
  getLocaleSearchValue,
} from "@/lib/locales";
import { cn } from "@/lib/utils";

interface Props {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * Searchable locale picker used by translation/site language forms.
 * Stores the ISO tag value but renders a friendlier flag + language label.
 */
export function LanguageCombobox({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  id,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selectedOption = value ? getLocaleOption(value) : null;

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => formatLocaleLabel(a).localeCompare(formatLocaleLabel(b))),
    [options],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-10 w-full justify-between"
          disabled={disabled}
        >
          <span className="flex min-w-0 items-center gap-2 text-left">
            {value && <LocaleFlag tag={value} />}
            <span className="truncate">
              {value ? selectedOption?.label ?? value : placeholder ?? t("localePicker.placeholder")}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter>
          <Command.Input
            className="h-10 w-full border-b bg-transparent px-3 text-base text-foreground outline-none placeholder:text-muted-foreground"
            placeholder={searchPlaceholder ?? t("localePicker.searchPlaceholder")}
          />
          <Command.List className="ui-scrollbar max-h-72 overflow-y-auto p-1">
            <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
              {emptyText ?? t("localePicker.empty")}
            </Command.Empty>

            {sortedOptions.map((tag) => (
              <Command.Item
                key={tag}
                value={getLocaleSearchValue(tag)}
                onSelect={() => {
                  onChange(tag);
                  setOpen(false);
                }}
                className={cn(
                  "flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm",
                  "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <LocaleFlag tag={tag} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block truncate">{getLocaleOption(tag)?.label ?? tag}</span>
                    <span className="block text-xs text-muted-foreground">{tag}</span>
                  </span>
                </span>
                <Check className={cn("ml-2 h-4 w-4", value === tag ? "opacity-100" : "opacity-0")} />
              </Command.Item>
            ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
